import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  IconButton,
  CircularProgress,
  Paper
} from '@mui/material';
import {
  Close as CloseIcon,
  Timeline as PathwayIcon,
  CollectionsBookmark as CollectionIcon,
  ArrowForwardIos as ArrowIcon,
  School as SchoolIcon,
  Category as CategoryIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { get as getCollection } from '../../../../services/practice-collection-service';
import CollectionActionButtons from '../../../../components/common/CollectionActionButtons';

const getStatusColor = (status) => {
  switch (status) {
    case 'FAILED':
      return 'error';
    case 'MANUAL_REVIEW':
      return 'warning';
    case 'GENERATING_OUTLINE':
    case 'GENERATING_PRACTICES':
    case 'CREATING_COLLECTION':
      return 'info';
    case 'COMPLETED':
    case 'PUBLISHED':
      return 'success';
    default:
      return 'primary';
  }
};

const PathwayDetailsDialog = ({ pathway, open, onClose, onEdit, onDelete }) => {
  const navigate = useNavigate();
  const [collectionsDetails, setCollectionsDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !pathway || !pathway.collections || pathway.collections.length === 0) {
      setCollectionsDetails([]);
      return;
    }

    const fetchCollections = async () => {
      setLoading(true);
      setError(null);
      try {
        const details = await Promise.all(
          pathway.collections.map(async (id) => {
            try {
              const data = await getCollection(id);
              return { id, data, success: true };
            } catch (err) {
              console.error(`Failed to load collection ${id}:`, err);
              return { id, success: false, error: err.message };
            }
          })
        );
        setCollectionsDetails(details);
      } catch (err) {
        setError('Failed to load some of the pathway collections.');
      } finally {
        setLoading(false);
      }
    };
    fetchCollections();
  }, [pathway, open]);

  const handleCollectionActionComplete = async (collectionId, action) => {
    try {
      const data = await getCollection(collectionId);
      setCollectionsDetails(prev => 
        prev.map(col => col.id === collectionId ? { ...col, data, success: true } : col)
      );
    } catch (err) {
      console.error(`Failed to update collection details after ${action}:`, err);
    }
  };

  if (!pathway) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: 24,
          overflow: 'hidden'
        }
      }}
    >
      {/* Dialog Header */}
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <PathwayIcon />
          <Typography variant="h6" fontWeight="bold" component="div">
            Pathway: {pathway.name}
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: 'primary.contrastText',
            '&:hover': { bgcolor: 'primary.dark' }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Dialog Content */}
      <DialogContent dividers sx={{ p: 3, bgcolor: 'background.default' }}>
        {/* Metadata section */}
        <Box sx={{ mb: 3 }}>
          <GridContainer>
            <MetaItem label="Unique Code" value={pathway.code} />
            <MetaItem label="Status" value={pathway.status} isChip chipColor={pathway.status === 'ACTIVE' ? 'success' : 'default'} />
            <MetaItem label="Created By" value={pathway.createdBy} />
          </GridContainer>

          {pathway.description && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 2, borderLeft: '4px solid', borderColor: 'primary.main' }}>
              <Typography variant="subtitle2" color="text.secondary" fontWeight="bold">
                Description
              </Typography>
              <Typography variant="body2" color="text.primary" sx={{ mt: 0.5 }}>
                {pathway.description}
              </Typography>
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Collections Section */}
        <Box>
          <Typography variant="h6" fontWeight="bold" color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CollectionIcon color="primary" />
            Path Collections ({pathway.collections.length})
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Typography color="error" variant="body2">{error}</Typography>
          ) : pathway.collections.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', bgcolor: 'background.paper', borderRadius: 2 }}>
              <Typography variant="body1" color="text.secondary">
                No collections have been added to this pathway yet.
              </Typography>
            </Paper>
          ) : (
            <List sx={{ width: '100%', bgcolor: 'background.paper', borderRadius: 2, p: 0 }} subheader={<li />}>
              {collectionsDetails.map((col, index) => {
                if (!col.success) {
                  return (
                    <Paper
                      key={col.id}
                      variant="outlined"
                      sx={{ p: 2, mb: 1, borderColor: 'error.light', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    >
                      <Typography color="error" variant="body2">
                        Failed to load collection ID: {col.id}
                      </Typography>
                      <Button size="small" variant="text" onClick={() => navigate(`/practice-collections/${col.id}`)}>
                        Go to ID page
                      </Button>
                    </Paper>
                  );
                }

                const collection = col.data;
                return (
                  <React.Fragment key={collection.id}>
                    {index > 0 && <Divider />}
                    <ListItem
                      button
                      onClick={() => {
                        onClose();
                        navigate(`/practice-collections/${collection.id}`);
                      }}
                      sx={{
                        py: 2,
                        px: 3,
                        transition: 'all 0.2s',
                        '&:hover': {
                          bgcolor: 'action.hover',
                          '& .arrow-icon': {
                            transform: 'translateX(4px)',
                            color: 'primary.main'
                          }
                        }
                      }}
                    >
                      <ListItemText
                        secondaryTypographyProps={{ component: 'div' }}
                        primary={
                          <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
                            {collection.title}
                          </Typography>
                        }
                        secondary={
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1, alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.8rem', color: 'text.secondary' }}>
                              <SchoolIcon sx={{ fontSize: 16 }} />
                              <span>{collection.schoolLevel}</span>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.8rem', color: 'text.secondary' }}>
                              <CategoryIcon sx={{ fontSize: 16 }} />
                              <span>{(collection.type || '').replace('_', ' ')}</span>
                            </Box>
                            <Chip
                              label={collection.status}
                              size="small"
                              color={getStatusColor(collection.status)}
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                            <CollectionActionButtons
                              collectionId={collection.id}
                              status={collection.status}
                              onActionComplete={(action) => handleCollectionActionComplete(collection.id, action)}
                              size="small"
                              sx={{ ml: 1 }}
                            />
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton edge="end" className="arrow-icon" sx={{ transition: 'transform 0.2s', color: 'text.secondary' }}>
                          <ArrowIcon fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, bg: 'background.paper' }}>
        {pathway.status === 'DRAFT' && (
          <Box sx={{ mr: 'auto', display: 'flex', gap: 1 }}>
            {onEdit && (
              <Button
                onClick={() => onEdit(pathway)}
                color="primary"
                variant="outlined"
                startIcon={<EditIcon />}
              >
                Edit Pathway
              </Button>
            )}
            {onDelete && (
              <Button
                onClick={() => onDelete(pathway)}
                color="error"
                variant="outlined"
                startIcon={<DeleteIcon />}
              >
                Delete Pathway
              </Button>
            )}
          </Box>
        )}
        <Button onClick={onClose} color="primary" variant="contained" sx={{ px: 4 }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const GridContainer = ({ children }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
      gap: 2,
      mb: 2
    }}
  >
    {children}
  </Box>
);

const MetaItem = ({ label, value, isChip, chipColor }) => (
  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
    <Typography variant="caption" color="text.secondary" fontWeight="500">
      {label}
    </Typography>
    {isChip ? (
      <Box>
        <Chip label={value} color={chipColor} size="small" />
      </Box>
    ) : (
      <Typography variant="body2" fontWeight="bold" color="text.primary">
        {value || 'N/A'}
      </Typography>
    )}
  </Paper>
);

export default PathwayDetailsDialog;
